import {
  fork,
  all,
  call,
  put,
  take,
  takeEvery,
  select,
  delay,
} from 'typed-redux-saga/macro';
import { isArray, noop } from 'lodash';
import { modules } from '@veritone/glc-redux';
import { fetchGraphQLApi } from '@helpers/callApi';
import { actionTryPermission } from '@user-permissions';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { CaseId } from '@common-modules/universal/models/Brands';
import uploadFilesChannel, {
  UploadDescriptor,
} from '@common/shared/uploadFilesChannel';
import { actionCloseFilePicker } from '@redact-modules/mainPage/actions';
import {
  UPLOAD_REQUEST,
  uploadProgress,
  uploadComplete,
  endPick,
} from './index';
import { ArrayOrSingle } from 'ts-essentials';

const { auth: authModule, config: configModule } = modules;

// interface Action<P, M = unknown> {
//   type: string;
//   payload: P;
//   meta: M;
// }

export interface UploadResult {
  key: string;
  bucket: string;
  expiresInSeconds: number;
  fileName: string;
  size: number;
  type: string;
  error: any;
  unsignedUrl: string | null;
  getUrl: string | null;
}

export function* finishUpload({
  id,
  govQARequestId,
  foiaXpressRequestId,
  casepointRequestId,
  nuixRequestId,
  exterroRequestId,
  result,
  status: { warning, error },
  callback,
  caseId,
  settingsProfileId,
}: {
  id: string;
  govQARequestId?: string;
  foiaXpressRequestId?: string;
  casepointRequestId?: string;
  nuixRequestId?: string;
  exterroRequestId?: string;
  result: UploadResult[] | null;
  status: {
    warning?: any;
    error?: any;
  };
  callback: () => void;
  caseId?: CaseId;
  settingsProfileId?: string;
}) {
  yield* put(
    uploadComplete(
      id,
      result || [],
      // TODO: need to type these better
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      { warning, error },
      {
        govQARequestId,
        foiaXpressRequestId,
        casepointRequestId,
        nuixRequestId,
        exterroRequestId,
      },
      caseId,
      settingsProfileId
    )
  );
  // fixme -- handle this better
  yield* delay(warning || error ? 1500 : 500);
  yield* put(endPick(id));
  // Need to type warning better
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  yield* call(callback, result, { warning, error, cancelled: false });
}

export function* uploadFileSaga({
  id,
  govQARequestId,
  foiaXpressRequestId,
  casepointRequestId,
  nuixRequestId,
  exterroRequestId,
  fileOrFiles,
  callback = noop,
  caseId,
  settingsProfileId,
}: {
  id: string;
  govQARequestId?: string;
  foiaXpressRequestId?: string;
  casepointRequestId?: string;
  nuixRequestId?: string;
  exterroRequestId?: string;
  fileOrFiles: ArrayOrSingle<File>;
  callback: () => void;
  caseId?: CaseId;
  settingsProfileId?: string;
}) {
  const files = isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  // `key` is intentionally not sent: it is deprecated (VE-26306) and the
  // backend mints a unique key itself. The returned key is used only as an
  // opaque per-file identifier for progress events, never to build a path.
  const getUrlQuery = `query urls {
        getSignedWritableUrl(expiresInSeconds: 43200) {
          url
          key
          bucket
          expiresInSeconds
          getUrl
          unsignedUrl
        }
      }`;
  interface getUrlResponse {
    getSignedWritableUrl: {
      url: string;
      key: string;
      bucket: string;
      expiresInSeconds: number;
      getUrl: string;
      unsignedUrl: string;
    };
  }

  const config = yield* select(configModule.getConfig);
  // config correct type is Window['config'] but returns Veritone BaseConfigState
  const { apiRoot, graphQLEndpoint, veritoneAppId } = config;
  const graphQLUrl = `${apiRoot}/${graphQLEndpoint}`;
  const sessionToken = yield* select(authModule.selectSessionToken);
  const oauthToken = yield* select(authModule.selectOAuthToken);

  // get a signed URL for each object to be uploaded, in bounded batches.
  //
  // This used to be one `all()` over every file, which was fine while the
  // picker capped a selection at five. The per-type caps (VE-28989) let
  // thirty files through (25 documents plus 5 images/audio/video), and firing
  // every GraphQL call at once makes the burst grow with the cap. Batching
  // keeps the in-flight count flat regardless of selection size.
  //
  // Order is preserved, which matters: `uploadDescriptors` is zipped against
  // `files` by index further down. Failure semantics are unchanged too — `all`
  // rejects on the first failure within a batch and the catch below still owns
  // the whole operation, so a late batch failing aborts the upload exactly as
  // a late element used to.
  const SIGNED_URL_BATCH_SIZE = 10;
  let signedWritableUrlResponses;
  try {
    signedWritableUrlResponses = [];
    for (
      let offset = 0;
      offset < files.length;
      offset += SIGNED_URL_BATCH_SIZE
    ) {
      const batch = yield* all(
        files.slice(offset, offset + SIGNED_URL_BATCH_SIZE).map(() =>
          call(fetchGraphQLApi<getUrlResponse>, {
            endpoint: graphQLUrl,
            query: getUrlQuery,
            variables: {},
            operationName: null,
            token: sessionToken || oauthToken,
            veritoneAppId,
          })
        )
      );
      signedWritableUrlResponses.push(...batch);
    }
  } catch (error) {
    return yield* finishUpload({
      id,
      govQARequestId,
      foiaXpressRequestId,
      casepointRequestId,
      nuixRequestId,
      exterroRequestId,
      result: null,
      status: { error },
      callback: function () {},
      caseId,
      settingsProfileId,
    });
  }

  let uploadDescriptors: UploadDescriptor[] = [];
  try {
    // Collected across the batch and reported once — warning per response would
    // emit one identical line per file on a large upload.
    const nonFatalMessages = new Set<string>();

    uploadDescriptors = signedWritableUrlResponses.map(({ data, errors }) => {
      // A GraphQL response may legally carry both `data` and `errors` — a
      // partial success. Fail only when the signed URL itself is missing;
      // otherwise `errors` holds non-fatal diagnostics (such as the `key`
      // deprecation warning) and the upload can proceed on valid data.
      // Throwing here rather than skipping the entry keeps the batch failing
      // atomically and preserves the server's own message — a skipped entry
      // would instead surface uploadFilesChannel's descriptor-count error.
      // Note `data` itself may be an explicit null, so it is read optionally.
      const getSignedWritableUrl = data?.getSignedWritableUrl;

      if (!getSignedWritableUrl) {
        throw new Error(
          `Call to getSignedWritableUrl returned error: ${
            errors?.[0]?.message ?? 'response contained no signed URL'
          }`
        );
      }

      errors?.forEach((e) => {
        if (e?.message) {
          nonFatalMessages.add(String(e.message));
        }
      });

      return getSignedWritableUrl;
    });

    if (nonFatalMessages.size) {
      // Messages only: the raw error objects can carry `extensions`/`path`, and
      // console output is captured as a Sentry breadcrumb.
      console.warn(
        '[filePicker] getSignedWritableUrl returned non-fatal errors; continuing with the signed URLs.',
        [...nonFatalMessages]
      );
    }
  } catch (error) {
    return yield* finishUpload({
      id,
      govQARequestId,
      foiaXpressRequestId,
      casepointRequestId,
      nuixRequestId,
      exterroRequestId,
      result: null,
      status: { error },
      callback,
      caseId,
      settingsProfileId,
    });
  }

  let resultChan;
  try {
    resultChan = yield* call(uploadFilesChannel, uploadDescriptors, files);
  } catch (error) {
    return yield* finishUpload({
      id,
      govQARequestId,
      foiaXpressRequestId,
      casepointRequestId,
      nuixRequestId,
      exterroRequestId,
      result: null,
      status: { error },
      callback,
      caseId,
      settingsProfileId,
    });
  }

  const result: UploadResult[] = [];

  // refreshToken using sdk call
  window.aiware?.auth?.reportAppActivity?.();

  let lastRefreshToken = Date.now();

  while (result.length !== files.length) {
    const {
      progress = 0,
      error,
      success,
      file,
      descriptor: { key, bucket, expiresInSeconds, getUrl, unsignedUrl, type },
    } = yield* take(resultChan);

    if (success || error) {
      yield* put(uploadProgress(id, key, 100));

      result.push({
        key,
        bucket,
        expiresInSeconds,
        fileName: file.name,
        size: file.size,
        type: type || file.type,
        error: error || false,
        unsignedUrl: error ? null : unsignedUrl,
        getUrl: error ? null : getUrl,
      });
      continue;
    }

    yield* put(uploadProgress(id, key, progress));

    // refreshToken using sdk call after every 5 min to prevent logout during large file upload
    if (Date.now() - lastRefreshToken > 300_000) {
      window.aiware?.auth?.reportAppActivity?.();
      lastRefreshToken = Date.now();
    }
  }

  const isError = result.every((e) => e.error);
  const isWarning = !isError && result.some((e) => e.error);

  yield* finishUpload({
    id,
    govQARequestId,
    foiaXpressRequestId,
    casepointRequestId,
    nuixRequestId,
    exterroRequestId,
    result,
    status: {
      warning: isWarning ? 'Some files failed to upload.' : false,
      error: isError ? 'All files failed to upload.' : false,
    },
    callback,
    caseId,
    settingsProfileId,
  });
}

function* watchUploadRequest() {
  yield* takeEvery(
    UPLOAD_REQUEST,
    function* ({
      payload: {
        id,
        files: payloadFiles,
        callback,
        govQARequestId,
        foiaXpressRequestId,
        casepointRequestId,
        nuixRequestId,
        exterroRequestId,
        caseId,
        settingsProfileId,
      },
    }) {
      const files = validateFileTypes(payloadFiles);

      const MAX_FILE_SIZE = 104_857_600;
      const fileIsArray = Array.isArray(files);
      /* Check if there are non-empty files to upload */
      if (
        (fileIsArray && files.every((f) => f.size === 0)) ||
        (!fileIsArray && files.size === 0)
      ) {
        yield* put(actionCloseFilePicker());
        yield* put(
          enqueueSnackbar({
            message:
              'WARNING: Cannot upload files that are 0 bytes! Please ensure you are not dragging and dropping from a zip file preview! Files must be extracted/unzipped prior to uploading.',
            variant: 'error',
          })
        );
      } else {
        if (fileIsArray) {
          let emptyFiles = false;
          files.reduceRight<File[]>((_, f, i, a) => {
            if (f.size === 0) {
              emptyFiles = true;
              a.splice(i, 1);
            }
            return a;
          }, []);

          if (emptyFiles) {
            yield* put(
              enqueueSnackbar({
                message:
                  'WARNING: Cannot upload files that are 0 bytes! Please ensure you are not dragging and dropping from a zip file preview! Files must be extracted/unzipped prior to uploading.',
                variant: 'warning',
              })
            );
          }
        }

        const args = {
          id,
          govQARequestId,
          foiaXpressRequestId,
          casepointRequestId,
          nuixRequestId,
          exterroRequestId,
          fileOrFiles: files,
          callback,
          caseId,
          settingsProfileId,
        };

        /* Upload non-empty files */
        yield* put(
          actionTryPermission({
            permissionKey: 'allowNoUploadSizeLimit',
            onAllow: function* () {
              yield* call(uploadFileSaga, {
                id,
                govQARequestId,
                foiaXpressRequestId,
                casepointRequestId,
                nuixRequestId,
                exterroRequestId,
                fileOrFiles: files,
                callback,
                caseId,
                settingsProfileId,
              });
            },
            onBlock: function* () {
              if (
                (fileIsArray && files.every((f) => f.size < MAX_FILE_SIZE)) ||
                (!fileIsArray && files.size < MAX_FILE_SIZE)
              ) {
                yield* call(uploadFileSaga, args);
              } else {
                yield* put(actionCloseFilePicker());
                yield* put(
                  enqueueSnackbar({
                    message:
                      'Your file size must be below 100MB. Please try a smaller file.',
                    variant: 'error',
                  })
                );
              }
            },
          })
        );
      }
    }
  );
}

/* Fixes a bug where macOS and Windows cannot identify certain file types */
const validateFileTypes = (files: ArrayOrSingle<File>) => {
  const fixMissingTypes = (file: File) => {
    /* Check if file has extension .vob */
    if (file.name.match(/\.[0-9a-z]+$/i)?.[0]?.toLowerCase() === '.vob') {
      return new File([file], file.name, { type: 'video/mpeg' });
    } else if (!file.type) {
      return new File([file], file.name, { type: 'binary/octet-stream' });
    }
    return file;
  };

  if (!isArray(files)) {
    return fixMissingTypes(files);
  } else {
    files.forEach((file, i, fs) => (fs[i] = fixMissingTypes(file)));
    return files;
  }
};

export default function* root() {
  yield* all([fork(watchUploadRequest)]);
}
